'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { Send, Bot, User, Minus, X, LogOut } from 'lucide-react';
import styles from './twin.module.css';

type ThemeOptions = {
    background?: string;
    foreground?: string;
    accent?: string;
    accentHover?: string;
    surface?: string;
    surfaceBorder?: string;
};

type WidgetConfig = {
    apiUrl?: string;
    targetId?: string;
    theme?: ThemeOptions;
    assetBase?: string;
};

declare global {
    interface Window {
        __ObliqChatConfig?: WidgetConfig;
    }
}

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

type TwinProps = {
    onMinimize?: () => void;
    onClose?: () => void;
};

export default function Twin({ onMinimize, onClose }: TwinProps) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string>('');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const apiBase = useMemo(() => {
        const defaultUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
        if (typeof window === 'undefined') {
            return defaultUrl.replace(/\/$/, '');
        }
        const configured = window.__ObliqChatConfig?.apiUrl;
        const value = (configured || defaultUrl).trim();
        return value.endsWith('/') ? value.slice(0, -1) : value;
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const theme = window.__ObliqChatConfig?.theme;
        const el = containerRef.current;
        if (!theme || !el) return;

        const mapping: Record<keyof ThemeOptions, string> = {
            background: '--twin-background',
            foreground: '--twin-foreground',
            accent: '--twin-accent',
            accentHover: '--twin-accent-hover',
            surface: '--twin-surface',
            surfaceBorder: '--twin-surface-border',
        };

        for (const [key, cssVar] of Object.entries(mapping) as Array<[keyof NonNullable<typeof theme>, string]>) {
            const value = theme?.[key];
            if (value) {
                el.style.setProperty(cssVar, value);
            }
        }
    }, []);

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: input,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await fetch(`${apiBase}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: userMessage.content,
                    session_id: sessionId || undefined,
                }),
            });

            if (!response.ok) throw new Error('Failed to send message');

            const data = await response.json();

            if (!sessionId) {
                setSessionId(data.session_id);
            }

            const assistantMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: data.response,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error:', error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
            // Refocus the input after message is sent
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Check if avatar exists
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const avatarUrl = useMemo(() => {
        if (typeof window === 'undefined') {
            return '/avatar.png';
        }
        const base = window.__ObliqChatConfig?.assetBase;
        if (base) {
            return `${base.replace(/\/$/, '')}/avatar.png`;
        }
        return `${window.location.origin.replace(/\/$/, '')}/avatar.png`;
    }, []);

    const [hasAvatar, setHasAvatar] = useState(true);

    const resetConversation = () => {
        setMessages([]);
        setSessionId('');
        setInput('');
        setIsLoading(false);
    };

    const classNames = (...classes: Array<string | false | null | undefined>) =>
        classes.filter(Boolean).join(' ');

    return (
        <div ref={containerRef} className={`${styles.twin} ${styles.container}`}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerLogoShell}>
                        {hasAvatar ? (
                            <img
                                src={avatarUrl}
                                alt="Obliq logo"
                                className={styles.headerLogo}
                                onError={() => setHasAvatar(false)}
                            />
                        ) : (
                            <Bot className={styles.headerIcon} />
                        )}
                    </div>
                    <h2 className={styles.headerTitle}>
                        <span className={styles.headerBrand}>Obliq</span>
                        <span className={styles.headerSubtitle}>Virtual Assistant</span>
                    </h2>
                </div>
                {(onMinimize || onClose) && (
                    <div className={styles.headerActions}>
                        {onMinimize && (
                            <button
                                type="button"
                                className={styles.headerActionButton}
                                onClick={onMinimize}
                                aria-label="Minimize chat"
                            >
                                <Minus className={styles.headerActionIcon} />
                            </button>
                        )}
                        {onClose && (
                            <button
                                type="button"
                                className={`${styles.headerActionButton} ${styles.headerCloseButton}`}
                                onClick={() => setShowCloseConfirm(true)}
                                aria-label="Close chat"
                            >
                                <X className={styles.headerActionIcon} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Messages */}
            <div className={styles.messages}>
                {messages.length === 0 && (
                    <div className={styles.emptyState}>
                        {hasAvatar ? (
                            <img 
                                src={avatarUrl} 
                                alt="Digital Twin Avatar" 
                                className={styles.emptyAvatar}
                                onError={() => setHasAvatar(false)}
                            />
                        ) : (
                            <Bot className={styles.emptyIcon} />
                        )}
                        <p className={styles.emptyTitle}>You&apos;re speaking with Obliq.</p>
                        <p className={styles.emptySubtitle}>
                            Let&apos;s map the outcomes you want, check data readiness, and shape a 4–6 week pilot with the right governance.
                        </p>
                    </div>
                )}

                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={classNames(
                            styles.messageRow,
                            message.role === 'user' && styles.messageRowUser
                        )}
                    >
                        {message.role === 'assistant' && (
                            <div className={styles.messageAvatarShell}>
                                {hasAvatar ? (
                                    <img 
                                        src={avatarUrl} 
                                        alt="Digital Twin Avatar" 
                                        className={styles.assistantAvatar}
                                        onError={() => setHasAvatar(false)}
                                    />
                                ) : (
                                    <div className={styles.assistantAvatar}>
                                        <div className={styles.assistantFallbackShell}>
                                            <Bot className={styles.assistantFallback} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div
                            className={classNames(
                                styles.messageBubble,
                                message.role === 'user' ? styles.userBubble : styles.assistantBubble
                            )}
                        >
                            <p>{message.content}</p>
                            <p
                                className={classNames(
                                    styles.timestamp,
                                    message.role === 'user' && styles.timestampUser
                                )}
                            >
                                {message.timestamp.toLocaleTimeString()}
                            </p>
                        </div>

                        {message.role === 'user' && (
                            <div className={styles.messageAvatarShell}>
                                <div className={styles.userAvatar}>
                                    <User className={styles.userIcon} />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {isLoading && (
                    <div className={styles.messageRow}>
                        <div className={styles.messageAvatarShell}>
                            {hasAvatar ? (
                                <img
                                    src={avatarUrl}
                                    alt="Digital Twin Avatar"
                                    className={styles.assistantAvatar}
                                    onError={() => setHasAvatar(false)}
                                />
                            ) : (
                                <div className={styles.assistantAvatar}>
                                    <div className={styles.assistantFallbackShell}>
                                        <Bot className={styles.assistantFallback} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={styles.typingBubble}>
                            <div className={styles.typingDots}>
                                <div className={`${styles.typingDot} animate-bounce`} />
                                <div className={`${styles.typingDot} animate-bounce delay-100`} />
                                <div className={`${styles.typingDot} animate-bounce delay-200`} />
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {showCloseConfirm && onClose && (
                <div className={styles.closeConfirmOverlay} role="dialog" aria-modal="true">
                    <div className={styles.closeConfirmDialog}>
                        <div className={styles.closeConfirmIconShell}>
                            <LogOut className={styles.closeConfirmIcon} />
                        </div>
                        <p className={styles.closeConfirmTitle}>Do you really want to close the chat?</p>
                        <div className={styles.closeConfirmActions}>
                            <button
                                type="button"
                                className={styles.closeConfirmButton}
                                onClick={() => {
                                    resetConversation();
                                    setShowCloseConfirm(false);
                                    onClose();
                                }}
                            >
                                Close the chat
                            </button>
                            <button
                                type="button"
                                className={styles.closeConfirmCancel}
                                onClick={() => setShowCloseConfirm(false)}
                            >
                                Keep chatting
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Input */}
            <div className={styles.inputArea}>
                <div className={styles.inputRow}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Tell me the outcome you need or ask how we deliver secure AI in 4–6 weeks..."
                        className={styles.input}
                        disabled={isLoading}
                        autoFocus
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!input.trim() || isLoading}
                        className={styles.sendButton}
                    >
                        <Send className={styles.sendIcon} />
                    </button>
                </div>
            </div>
        </div>
    );
}
